<template>
  <div class="fstep" :key="counter">
    <div class="fstep__head">
      <h2 class="fstep__title">{{ $t("property.create.pictures.instructions") }}</h2>
    </div>

    <div class="fstep__card" :key="images.length">
      <label class="fstep__drop">
        <hero-icon :icon="PhotoIcon" :size="26" />
        <div class="fstep__drop-title">{{ $t('property.create.pictures.fields.file') }}</div>
        <div class="fstep__drop-sub">Seleccioná una o varias imágenes</div>
        <input type="file" accept="image/*" multiple @change="onPick" />
      </label>

      <div v-if="images.length" class="fstep__hint">
        Arrastrá las imágenes para ordenarlas.
      </div>

      <draggable
        v-model="images"
        :key="images.length"
        group="images"
        handle=".fstep__grip"
        @start="drag = true"
        @end="drag = false"
        @change="onMove"
        class="fstep__list"
      >
        <div
          v-for="(image, idx) in images"
          :key="image.id"
          class="fstep__img"
        >
          <span class="fstep__grip" title="Arrastrar para ordenar">
            <hero-icon :icon="Bars3Icon" :size="18" />
          </span>

          <div class="fstep__thumb">
            <img v-if="remoteUrl(image)" :src="remoteUrl(image)" alt="" />
          </div>

          <div class="fstep__info">
            <div class="fstep__name">
              {{
                image.name ||
                  $t("property.create.pictures.image_process.image_already_uploaded")
              }}
            </div>
            <div class="fstep__size">
              {{ (image.size / (1024 * 1024)).toFixed(2) }} MB
            </div>
            <v-progress-linear
              v-if="loading"
              :value="progress || 100"
              color="accent"
              rounded
              height="4"
              class="fstep__bar"
            ></v-progress-linear>
          </div>

          <button
            type="button"
            class="fstep__del"
            title="Eliminar"
            @click="removeImage(idx)"
          >
            <hero-icon :icon="TrashIcon" :size="16" />
          </button>
        </div>
      </draggable>

      <div v-if="uploaded" class="fstep__upmeta">
        {{ (uploaded.loaded / (1024 * 1024)).toFixed(2) }} MB /
        {{ (uploaded.total / (1024 * 1024)).toFixed(2) }} MB
      </div>
    </div>

    <div class="fstep__footer">
      <v-btn
        text
        color="primary"
        class="rounded-lg"
        :disabled="loading"
        :loading="loading"
        @click="$router.go(-1)"
      >
        {{ $t("action.close_and_continue_later") }}
      </v-btn>
      <v-btn
        color="accent"
        depressed
        class="rounded-lg"
        :disabled="loading"
        :loading="loading"
        @click="nextStep"
      >
        {{ $t("action.next") }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import { DeleteImage } from "@/graphql/mutations/images";
import { EditProperty } from "@/graphql/mutations/properties";
import { Property } from "@/graphql/queries/property";
import UploadService from "@/services/uploadFiles";
import draggable from "vuedraggable";
import HeroIcon from "@/components/shared/HeroIcon";
import PhotoIcon from "heroicons/24/outline/photo.svg";
import Bars3Icon from "heroicons/24/outline/bars-3.svg";
import TrashIcon from "heroicons/24/outline/trash.svg";

function comparer(otherArray) {
  return function(current) {
    return (
      otherArray.filter(function(other) {
        return other.name == current.name;
      }).length == 0
    );
  };
}

export default {
  data() {
    return {
      PhotoIcon,
      Bars3Icon,
      TrashIcon,
      drag: false,
      property: {},
      selectedFiles: [],
      sortingImages: [],
      message: "",
      progress: 0,
      counter: 0,
      uploaded: null,
      loaded: false,
      loading: false,
      images: []
    };
  },

  props: {
    fetchedProperty: {
      type: Object,
      required: true
    }
  },

  mounted() {
    this.fetchProperty();
  },

  methods: {
    fetchProperty() {
      this.$apollo
        .query({
          query: Property,
          variables: {
            id: this.fetchedProperty.id
          },
          fetchPolicy: "no-cache"
        })
        .then(res => {
          this.images = res.data.property.images;
          this.counter += 1;
        });
    },

    nextStep() {
      this.$emit("continue", this.fetchedProperty);
    },

    remoteUrl(image) {
      if (image.url) {
        return process.env.VUE_APP_RAILS_URL + image.url;
      }
    },

    removeImage(idx) {
      let id = this.images[idx].id;

      if (id) {
        this.$apollo
          .mutate({
            mutation: DeleteImage,
            variables: {
              input: {
                imageId: id
              }
            }
          })
          .then(res => {
            this.fetchProperty();
          });
      }

      if (!id) {
        this.images.splice(idx, 1);
        this.selectedFiles.splice(idx, 1);
      }

      this.selectedFiles = [];
    },

    onPick(e) {
      this.preview(Array.from(e.target.files));
      e.target.value = "";
    },

    preview(files) {
      var onlyNewFiles = files.filter(comparer(this.selectedFiles));

      if (onlyNewFiles.length) {
        this.images = [];
        this.loaded = false;

        var difference = this.selectedFiles.concat(onlyNewFiles);

        this.selectedFiles = difference;

        this.upload();
      }
    },

    edit() {
      this.loading = true;

      this.$apollo
        .mutate({
          mutation: EditProperty,
          variables: {
            input: {
              propertyId: this.fetchedProperty.id,
              attributes: {
                imagesAttributes: this.images
              }
            }
          }
        })
        .then(res => {
          this.fetchProperty();
          this.loading = false;
        });
    },

    upload() {
      var images = [];

      this.selectedFiles.forEach((file, i) => {
        images.push({
          name: file.name,
          size: file.size,
          image: file,
          lft: i + this.fetchedProperty.images.length
        });
      });

      this.loading = true;

      this.$apollo
        .mutate({
          mutation: EditProperty,
          variables: {
            input: {
              propertyId: this.fetchedProperty.id,
              attributes: {
                imagesAttributes: images
              }
            }
          }
        })
        .then(res => {
          this.fetchProperty();
          this.selectedFiles = [];
          this.loading = false;
        });
    },

    onMove() {
      this.images.forEach((image, i) => {
        delete image.__typename;
        delete image.url;
        delete image.thumbUrl;
        delete image.smallUrl;

        image.lft = i;
      });

      this.edit();
    }
  },

  components: { draggable, HeroIcon }
};
</script>

<style scoped>
.fstep__head { margin-bottom: 16px; }
.fstep__title {
  font-size: 22px;
  font-weight: 700;
  color: #1a1814;
  margin: 0;
  line-height: 1.2;
}

.fstep__card {
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 14px;
  padding: 22px;
}

/* upload dropzone */
.fstep__drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 26px;
  border: 1.5px dashed #dcd4c6;
  border-radius: 12px;
  background: #faf8f4;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.fstep__drop:hover { border-color: #b52118; background: #f5f1ea; }
.fstep__drop .hero-icon { color: #a39a8c; }
.fstep__drop-title { font-size: 14px; font-weight: 600; color: #1a1814; margin-top: 4px; }
.fstep__drop-sub { font-size: 12.5px; color: #756f64; }
.fstep__drop input { display: none; }

.fstep__hint { font-size: 12px; color: #a39a8c; margin: 14px 0 8px; }

/* image rows */
.fstep__list { display: flex; flex-direction: column; gap: 8px; }
.fstep__img {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 12px;
  padding: 8px 12px 8px 8px;
}
.fstep__grip { flex: none; display: flex; cursor: grab; color: #c4bcae; }
.fstep__grip:active { cursor: grabbing; }
.fstep__thumb {
  flex: none;
  width: 60px;
  height: 60px;
  border-radius: 9px;
  overflow: hidden;
  background: #ece7de;
}
.fstep__thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.fstep__info { flex: 1; min-width: 0; }
.fstep__name {
  font-size: 13.5px;
  font-weight: 600;
  color: #1a1814;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fstep__size { font-size: 12px; color: #756f64; margin-top: 1px; }
.fstep__bar { margin-top: 7px; max-width: 220px; }
.fstep__del {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: none;
  cursor: pointer;
  color: #756f64;
  transition: background 0.15s ease, color 0.15s ease;
}
.fstep__del:hover { background: #fbeae8; color: #c0392b; }

.fstep__upmeta { font-size: 12px; color: #a39a8c; text-align: right; margin-top: 8px; }

.fstep__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}
</style>
